import { useCallback, useEffect, useRef, useState } from "react";

import { TextFieldProps, TextFieldVariants } from "@mui/material";

import Resize from "@public/contact/resize.svg";

import { BottomLineStyled, TextFieldStyled } from "./styles.styled";

export type TextFieldWithCounterProps<
  Variant extends TextFieldVariants = TextFieldVariants,
> = TextFieldProps<Variant> & {
  maxLength: number;
};

export const TextFieldWithCounter = <
  Variant extends TextFieldVariants = TextFieldVariants,
>({
  maxLength,
  onChange,
  ...props
}: TextFieldWithCounterProps<Variant>) => {
  const contentLength = props.value?.toString().length ?? 0;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      if (e.target.value.length <= maxLength) {
        onChange(e);
      }
    }
  };

  const threshold = 20;
  const minHeight = 5;
  const maxHeight = 20;

  const [isResizing, setIsResizing] = useState(false);
  const [height, setHeight] = useState(minHeight);
  const ref = useRef<HTMLDivElement>(null);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isResizing && ref.current) {
        const offset = e.clientY - ref.current.getBoundingClientRect().y;

        if (offset > threshold) {
          setHeight(height => Math.min(height + 1, maxHeight));
        } else if (offset < -threshold) {
          setHeight(height => Math.max(height - 1, minHeight));
        }
      }
    },
    [isResizing, threshold, maxHeight, minHeight],
  );

  const handleMouseUp = useCallback((e: MouseEvent) => {
    setIsResizing(false);
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLImageElement>) => {
      setIsResizing(true);
      e.preventDefault();
    },
    [],
  );

  useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return (
    <div className="relative">
      <TextFieldStyled
        {...props}
        onChange={handleChange}
        className="w-full"
        minRows={height}
        maxRows={height}
      />
      <BottomLineStyled error={!!props.error}>
        {contentLength}/{maxLength}
        <div
          ref={ref}
          onMouseDown={handleMouseDown}
          className="cursor-move inline-block pointer-events-auto"
        >
          <Resize />
        </div>
      </BottomLineStyled>
    </div>
  );
};
